import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Radio, Flag, Heart, AlertTriangle, Wrench } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { crushshyAdminApi } from '../../../services/api';
import { useToast } from '../../../hooks/use-toast';
import { useCrushShyT } from '../../../hooks/useCrushShyT';
import CrushShyPageHeader from './CrushShyPageHeader';
import { crushCard } from './crushshyTheme';

const CrushShyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const { toast } = useToast();
  const t = useCrushShyT();

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    try {
      const [dashRes, usersRes] = await Promise.all([
        crushshyAdminApi.getDashboard(),
        crushshyAdminApi.getUsers(),
      ]);
      setStats(dashRes.data.stats);
      setUsers(usersRes.data.users?.slice(0, 6) || []);
    } catch {
      toast({ title: t('toastLoadDashboardError'), variant: 'destructive' });
    }
  };

  const statCards = stats
    ? [
        { label: t('statUsers'), value: stats.totalUsers, icon: Users, color: 'text-pink-400' },
        { label: t('statActive'), value: stats.activeUsers, icon: Radio, color: 'text-emerald-400' },
        { label: t('statLive'), value: stats.liveUsers, icon: Heart, color: 'text-purple-400' },
        { label: t('statOpenReports'), value: stats.openReports, icon: Flag, color: 'text-amber-400' },
        { label: t('statIncomplete'), value: stats.incompleteProfiles, icon: AlertTriangle, color: 'text-orange-400' },
        { label: t('statMatches'), value: stats.totalMatches, icon: Heart, color: 'text-cyan-400' },
      ]
    : [];

  const chartData = users.map((u) => ({
    name: u.name?.split(' ')[0] || u.id,
    matchs: u.matchesCount || 0,
  }));

  return (
    <div className="p-6 sm:p-8 max-w-7xl">
      <CrushShyPageHeader
        title={t('dashboardTitle')}
        subtitle={t('dashboardSubtitle')}
        action={
          stats?.maintenanceMode ? (
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 text-sm font-medium">
              <Wrench className="w-4 h-4" /> {t('dashboardMaintenance')}
            </span>
          ) : null
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className={`${crushCard} p-4`}
            >
              <Icon className={`w-5 h-5 mb-2 ${card.color}`} />
              <p className="text-2xl font-bold text-white">{card.value}</p>
              <p className="text-xs text-gray-500 mt-1">{card.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className={`${crushCard} p-6`}>
          <h2 className="text-lg font-semibold text-white mb-4">{t('chartMatchesTitle')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: '#1a1024', border: '1px solid rgba(255,77,141,0.2)' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Bar dataKey="matchs" fill="#FF4D8D" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className={`${crushCard} p-6`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-white">{t('recentActivity')}</h2>
            <Link to="/admin/crushshy/users" className="text-sm text-pink-400 hover:text-pink-300">
              {t('seeAll')}
            </Link>
          </div>
          <ul className="space-y-3">
            {users.map((u) => (
              <li
                key={u.id}
                className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
              >
                <div>
                  <p className="font-medium text-white">{u.name}</p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                </div>
                <StatusBadge status={u.status} />
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        <QuickLink to="/admin/crushshy/users" label={t('quickUsers')} />
        <QuickLink to="/admin/crushshy/reports" label={t('quickReports')} />
        <QuickLink to="/admin/crushshy/settings" label={t('quickSettings')} />
      </div>
    </div>
  );
};

const QuickLink = ({ to, label }) => (
  <Link
    to={to}
    className={`${crushCard} block p-4 text-center text-sm font-medium text-pink-300 hover:text-pink-200 hover:border-pink-500/30 transition-colors`}
  >
    {label}
  </Link>
);

export const StatusBadge = ({ status }) => {
  const t = useCrushShyT();
  const styles = {
    active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    suspended: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    banned: 'bg-red-500/15 text-red-300 border-red-500/30',
  };
  const labels = {
    active: t('statusActive'),
    suspended: t('statusSuspended'),
    banned: t('statusBanned'),
  };
  return (
    <span className={`text-xs px-2.5 py-1 rounded-full border ${styles[status] || styles.active}`}>
      {labels[status] || status}
    </span>
  );
};

export default CrushShyDashboard;
